/* ============================================================================
 * stockistParse.js — distributor-sheet parsing & cleaning (browser + tests)
 * ----------------------------------------------------------------------------
 * Shared logic for the admin "Import sheet" review. Mirrors scripts/build-seed.js
 * (the one-off Node seed builder) so the offline seed and the live import behave
 * identically: same name cleaning, same exclusions, same type guesses.
 * ==========================================================================*/

const MONTHS = new Set([
  'january','february','march','april','may','june',
  'july','august','september','october','november','december',
])

// Rows that are NOT stockists — flagged/excluded, never silently added.
export const EXCLUDE_PATTERNS = [
  { re: /cash customer/i,                     reason: 'Cash account' },
  { re: /internal account|stock shrinkage/i,  reason: 'Internal account' },
  { re: /staff account/i,                     reason: 'Staff account' },
  { re: /treasury wa/i,                        reason: 'Wholesale / corporate' },
]

const VENUE_KW = [
  'bar','hotel','brewing','brewery','bistro','restaurant','kitchen','grill',
  'paddock','tavern','rebelle','rockpool','king somm','lums','crown perth',
  'gibney','la vigna','next door','corner dairy','samsons','saint wine','mane',
  'karalee','long point','majuba',
]
const BOTTLE_KW = [
  'cellar','cellars','cellarbrations','liquor','bottle','wine store','wine box',
  'cru','re store','restore','de vine','old bridge','porters','scarborough',
  'broadfair','green street','copper and oak','oxford','dunsborough','hillarys',
  'mounts bay','freo doctor','liberty','boatshed','wine thief','deli',
]

function hasKeyword(name, keywords) {
  const n = name.toLowerCase()
  return keywords.some(k =>
    new RegExp(`\\b${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(n))
}

export function guessType(name) {
  if (hasKeyword(name, BOTTLE_KW)) return 'bottle_shop'
  if (hasKeyword(name, VENUE_KW))  return 'venue'
  return 'bottle_shop'
}

export function excludeReason(rawName) {
  const ex = EXCLUDE_PATTERNS.find(p => p.re.test(rawName))
  return ex ? ex.reason : null
}

// Strip distributor noise: (WA), "30 EOM" terms, "- eBev", ALM account wrappers.
export function cleanName(raw) {
  let s = (raw || '').trim()
  const alm = s.match(/^\d+\s+(.*?)\s*-\s*ALM \(WET Exempt\)\s*$/i)
  if (alm) s = alm[1].trim()
  s = s.replace(/\s*-\s*eBev\s*$/i, '')
  s = s.replace(/\s*\(WA\)\s*/gi, ' ')
  s = s.replace(/\s*\b\d+\s*EOM\b\s*/gi, ' ')
  if (/@/.test(s)) s = s.replace(/@/g, ' ')
  s = s.replace(/\s{2,}/g, ' ').trim()
  if (s && s === s.toUpperCase()) {
    s = s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
  }
  return s.replace(/\s{2,}/g, ' ').trim()
}

export function deriveSuburb(cleanedName) {
  const dash = cleanedName.match(/-\s*([A-Za-z' ]+)$/)
  if (dash) return dash[1].trim()
  const KNOWN = [
    'City Beach','Osborne Park','Guildford','Scarborough','Cottesloe','Nedlands',
    'Melville','Como','Inglewood','Wembley Downs','Wembley','Esperance',
  ]
  for (const k of KNOWN) if (cleanedName.includes(k)) return k
  return ''
}

// The chain base name (text before " - "), lowercased — used to detect
// multi-branch names that can't be disambiguated by name alone.
export function baseName(name) {
  return name.split(' - ')[0].trim().toLowerCase()
}

// ── Delimited parsing (CSV upload OR pasted rows; tab or comma) ──────────────
export function parseDelimited(text) {
  const rows = []
  let row = [], field = '', inQ = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++ }
      else if (c === '"') inQ = false
      else field += c
    } else if (c === '"') inQ = true
    else if (c === ',' || c === '\t') { row.push(field); field = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(field); rows.push(row); row = []; field = ''
    } else field += c
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row) }
  return rows
}

/**
 * Collapse a distributor sheet (array-of-rows, or raw text) to unique stockists.
 * Ignores the product column; drops month-section headers; excludes non-stockist
 * rows (flagged separately). Returns { kept, excluded }.
 *   kept:     [{ name, suburb, type, rawName }]
 *   excluded: [{ rawName, reason }]
 */
export function collapseSheet(input) {
  const rows = typeof input === 'string' ? parseDelimited(input) : input
  const kept = new Map()
  const excluded = new Map()

  for (const r of rows) {
    const rawName = (Array.isArray(r) ? r[0] : r || '').trim()
    if (!rawName) continue
    if (MONTHS.has(rawName.toLowerCase())) continue
    if (/^(stockist|customer|account|name)\b/i.test(rawName) && rows.indexOf(r) === 0) continue // header

    const reason = excludeReason(rawName)
    if (reason) { if (!excluded.has(rawName)) excluded.set(rawName, reason); continue }

    const name = cleanName(rawName)
    if (!name) continue
    if (!kept.has(name.toLowerCase())) {
      kept.set(name.toLowerCase(), {
        name,
        suburb: deriveSuburb(name),
        type:   guessType(name),
        rawName,
      })
    }
  }

  return {
    kept:     [...kept.values()].sort((a, b) => a.name.localeCompare(b.name)),
    excluded: [...excluded.entries()].map(([rawName, reason]) => ({ rawName, reason })),
  }
}
